import { gql } from '@apollo/client';

// --- Fragments ---

export const PLAYLIST_LIST_FIELDS = gql`
  fragment PlaylistListFields on Playlist {
    _id
    name
    teamId
    path
    tags
    color
    totalDuration
    createdAt
    lastUpdatedDate
  }
`;

export const PLAYLIST_DETAIL_FIELDS = gql`
  fragment PlaylistDetailFields on PlaylistDetailResponse {
    _id
    name
    teamId
    path
    tags
    targetTags
    color
    totalDuration
    groupId
    isDisable
    sharedLink
    createdAt
    lastUpdatedDate
    assets {
      _id
      uuid
      filename
      duration
      srcDuration
      normalDuration
      slideDuration
      type
      fileType
      appType
      webType
      youtubeType
      subType
      commonType
      webLink
      thumbnail
      bucket
      fileSize
      transition
      speed
      speedValue
      playEvery
      targetTags
      scheduleId
      orientation
      isHide
      isCaption
      assetExpired
      AWSS3ID
      status
      engage
      canOffline
      subPlaylistControl
      subPlaylistData {
        maxDurationPerItem
        rotateXItems
      }
      applyToData {
        currentApplyTo
        currentType
        currentDeviceIds
        currentTags
      }
      resumeOnNextPlay
    }
    options {
      scaleImage
      scaleVideo
      scaleDocument
      shuffle
      defaultTransition
      speed
      speedValue
      durationLimit
      backgroundAudioType
      backgroundAudio
      backgroundAudioAWSS3ID
      backgroundAudioName
      resumeOnNextPlay
      orientation
      scheduleId
      targetTagsDefault
      subPlaylistControl
      subPlaylistData {
        maxDurationPerItem
        rotateXItems
      }
      applyToData {
        currentApplyTo
        currentType
        currentDeviceIds
        currentTags
      }
    }
  }
`;

// --- Queries ---

// NOTE: The PLAYLISTS_QUERY intentionally inlines field selections instead of
// using the ...PlaylistListFields fragment. The backend's playlist resolver uses
// extractFields(info.fieldNodes) to build MongoDB projections, but it doesn't
// resolve GraphQL fragment spreads — it reads AST selection names directly.
// Using a named fragment here would cause the backend to project only
// {PlaylistListFields: 1} instead of the actual fields, returning empty results.
export const PLAYLISTS_QUERY = gql`
  query Playlists(
    $first: Int
    $after: String
    $teamId: String
    $query: QueryPlaylistInput
    $sort: PlaylistSort
  ) {
    playlists(first: $first, after: $after, teamId: $teamId, query: $query, sort: $sort) {
      page {
        edges {
          cursor
          node {
            _id
            name
            teamId
            path
            tags
            color
            totalDuration
            sharedLink
            createdAt
            lastUpdatedDate
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      pageData {
        total
      }
      extensions {
        groups {
          _id
          name
          type
          path
          teamId
          isFolderSecurityEdit
          isFolderSecurityView
          securities {
            userId
            role
          }
          securityTeams
          sharedLink
        }
      }
    }
  }
`;

// Batch fetch playlists by IDs for realtime subscription cache sync.
// Returns Playlist[] (same __typename as list) so Apollo auto-merges into list cache.
export const PLAYLISTS_BY_IDS = gql`
  query PlaylistsByIds($ids: [String!]!, $teamId: String) {
    playlistsByIds(ids: $ids, teamId: $teamId) {
      _id
      name
      teamId
      path
      tags
      color
      totalDuration
      createdAt
      lastUpdatedDate
    }
  }
`;

export const GET_PLAYLIST_DETAIL = gql`
  query GetPlaylistDetail($id: String!, $teamId: String) {
    getPlaylistDetail(_id: $id, teamId: $teamId) {
      ...PlaylistDetailFields
    }
  }
  ${PLAYLIST_DETAIL_FIELDS}
`;

export const PLAYLIST_TARGET_TAGS = gql`
  query PlaylistTargetTags($teamId: String) {
    playlistTargetTags(teamId: $teamId)
  }
`;

// Backend returns GraphQLJSONObject with shape:
// { devices: [{_id, deviceName, path}, ...] }
export const PLAYLIST_DEVICE_WHERE_USED = gql`
  query PlaylistDeviceWhereUsed($id: String!, $teamId: String) {
    playlistDeviceWhereUsed(_id: $id, teamId: $teamId)
  }
`;

// Backend returns GraphQLJSONObject with shape:
// { listSchedules: [{_id, name, path}], hasMore, currentPage, totalSchedules }
export const PLAYLIST_SCHEDULES_WHERE_USED = gql`
  query PlaylistSchedulesByWhereUsed($id: String!, $currentPage: Float!, $teamId: String) {
    playlistSchedulesByWhereUsed(_id: $id, currentPage: $currentPage, teamId: $teamId)
  }
`;

// Backend uses Relay ConnectionArgs (first/after) + returns AssetResponse with
// page.edges[].node.{_id, originalFileName, path} and pageData.total
export const PLAYLIST_SPLIT_SCREENS_WHERE_USED = gql`
  query PlaylistSplitScreensWhereUsed($id: String!, $first: Int, $after: String, $teamId: String) {
    playlistSplitScreensWhereUsed(_id: $id, first: $first, after: $after, teamId: $teamId) {
      page {
        edges {
          cursor
          node {
            _id
            originalFileName
            path
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      pageData {
        total
      }
    }
  }
`;

// --- Mutations ---

export const SAVE_PLAYLIST = gql`
  mutation SavePlaylist($teamId: String, $payload: PlaylistInput!) {
    savePlaylist(teamId: $teamId, payload: $payload) {
      _id
      name
      teamId
      isDisable
      path
      tags
      targetTags
      groupId
      createdAt
      lastUpdatedDate
      sharedLink
      assets {
        _id
        uuid
        filename
        appType
        webType
        subType
        commonType
        fileType
        engage
        status
        isHide
        isCaption
        assetExpired
        youtubeType
        AWSS3ID
        type
        duration
        srcDuration
        normalDuration
        slideDuration
        thumbnail
        bucket
        fileSize
        transition
        speed
        speedValue
        webLink
        canOffline
        applyToData {
          currentApplyTo
          currentDeviceIds
          currentTags
          currentType
        }
        targetTags
        scheduleId
        playEvery
        subPlaylistControl
        subPlaylistData {
          maxDurationPerItem
          rotateXItems
        }
        resumeOnNextPlay
        orientation
      }
      options {
        scaleImage
        scaleVideo
        scaleDocument
        shuffle
        defaultTransition
        speed
        speedValue
        durationLimit
        backgroundAudioType
        backgroundAudio
        backgroundAudioAWSS3ID
        backgroundAudioName
        resumeOnNextPlay
        orientation
        scheduleId
        targetTagsDefault
        subPlaylistControl
        subPlaylistData {
          maxDurationPerItem
          rotateXItems
        }
        applyToData {
          currentApplyTo
          currentType
          currentDeviceIds
          currentTags
        }
      }
      color
      totalDuration
    }
  }
`;

export const ADD_PLAYLIST_ITEMS = gql`
  mutation AddPlaylistItems($teamId: String, $id: String!, $payload: AddPlaylistItemsInput!) {
    addPlaylistItems(teamId: $teamId, _id: $id, payload: $payload) {
      _id
    }
  }
`;

export const REMOVE_PLAYLIST_ITEMS = gql`
  mutation RemovePlaylistItems($teamId: String, $id: String!, $payload: RemovePlaylistItemsInput!) {
    removePlaylistItems(teamId: $teamId, _id: $id, payload: $payload) {
      _id
    }
  }
`;

export const UPDATE_PLAYLIST_ITEMS = gql`
  mutation UpdatePlaylistItems($teamId: String, $id: String!, $payload: UpdatePlaylistItemsInput!) {
    updatePlaylistItems(teamId: $teamId, _id: $id, payload: $payload) {
      _id
    }
  }
`;

export const CLONE_PLAYLIST = gql`
  mutation ClonePlaylist($id: String!, $teamId: String) {
    clonePlaylist(_id: $id, teamId: $teamId) {
      _id
      name
      path
      teamId
      color
      createdAt
      lastUpdatedDate
    }
  }
`;

export const ADD_ITEM_PLAYLISTS = gql`
  mutation AddItemPlaylists($ids: [String!]!, $payload: AddPlaylistItemsInput!, $teamId: String) {
    addItemPlaylists(ids: $ids, payload: $payload, teamId: $teamId)
  }
`;

export const UPDATE_OPTION_PLAYLISTS = gql`
  mutation UpdateOptionPlaylists(
    $ids: [String!]!
    $payload: PlaylistOptionInput!
    $teamId: String
  ) {
    updateOptionPlaylists(ids: $ids, payload: $payload, teamId: $teamId)
  }
`;

export const VALIDATE_PLAYLIST_IN_PLAYLIST = gql`
  mutation ValidatePlaylistInPlaylist($teamId: String, $payload: ValidatePlaylistInPlaylistInput!) {
    validatePlaylistInPlaylist(teamId: $teamId, payload: $payload)
  }
`;

// Separate query for playlist folder groups — fetches folders at a specific path
export const PLAYLIST_OPERATION_REPORT = gql`
  query PlaylistOperationReport($_id: String!, $teamId: String) {
    playlistOperationReport(_id: $_id, teamId: $teamId)
  }
`;

// for folder tree navigation (e.g., MoveToFolderDialog). Uses the same `groups`
// resolver as DEVICE_GROUPS_QUERY / ASSET_GROUPS_QUERY, filtered by type PLAYLIST.
// Includes security fields needed for folder-level permission filtering.
// Supports filtering by name_contains and sorting by name/createdAt/lastUpdatedDate.
export const PLAYLIST_GROUPS_QUERY = gql`
  query PlaylistGroups(
    $query: QueryGroupInput
    $first: Int
    $after: String
    $sort: GroupSort
    $teamId: String
  ) {
    groups(query: $query, first: $first, after: $after, sort: $sort, teamId: $teamId) {
      page {
        edges {
          cursor
          node {
            _id
            name
            type
            path
            teamId
            isFolderSecurityEdit
            isFolderSecurityView
            securities {
              userId
              role
            }
            securityTeams
            securitiesDetail {
              name
              userId
              role
            }
            securityTeamsDetail {
              name
              _id
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      pageData {
        total
      }
    }
  }
`;

// Lightweight Atlas Search query for playlist picker combobox.
// Returns flat array (not Relay-paginated). Only fields needed for display.
export const SEARCH_PLAYLISTS_PICKER = gql`
  query SearchPlaylistsPicker($teamId: String, $input: SearchInput!) {
    searchPlaylists(teamId: $teamId, input: $input) {
      _id
      name
      path
      teamId
      color
    }
  }
`;

// Check if a playlist contains apps/engage content that requires a higher plan.
// Returns JSON: { status, type: 'appMeta'|'engage', assets: [{ appName }], isHaveAppMeta }
export const CHECK_PLAYLIST_PLAN_PERMISSION = gql`
  query CheckPlaylistHavePermissionAssignToScreen($_id: String!, $teamId: String) {
    checkPlaylistHavePermissionAssignToScreen(_id: $_id, teamId: $teamId)
  }
`;
